"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { Toaster } from "@/components/ui/sonner";
import { PanelShell } from "@/components/layout/PanelShell/PanelShell";
import { PanelSettingsDialog } from "@/modules/panel/components/PanelSettingsDialog";
import { AgentInstallDialog } from "@/modules/servers/components/AgentInstallDialog";
import { AppSidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { PanelProvider } from "@/providers/PanelProvider";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { usePanelController } from "@/modules/panel/hooks/usePanelController";

export default function PanelLayout({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { authState, authUser, logout: authLogout } = useAuth();
  const panel = usePanelController({ isAuthenticated: authState === "authenticated", authLogout });

  useEffect(() => {
    if (authState === "anonymous") router.replace("/login");
  }, [authState, router]);

  if (authState !== "authenticated") return null;

  return (
    <PanelShell
      sidebarCollapsed={panel.sidebarCollapsed}
      onSidebarCollapsedChange={panel.setSidebarCollapsed}
      sidebar={<AppSidebar active={panel.page} onChange={panel.navigateFromSidebar} authUser={authUser} onLogout={panel.logout} onOpenSettings={() => panel.setSettingsOpen(true)} />}
      topbar={<Topbar page={panel.page} />}
    >
      <PanelProvider controller={panel}>{children}</PanelProvider>
      <PanelSettingsDialog open={panel.settingsOpen} onOpenChange={panel.setSettingsOpen} proxyUrl={panel.settingsProxyUrl} onProxyUrlChange={panel.setSettingsProxyUrl} saving={panel.settingsSaving} onSave={panel.savePanelSettings} />
      <AgentInstallDialog command={panel.agentInstallCommand} onOpenChange={(open) => !open && panel.setAgentInstallCommand(null)} />
      <Toaster position="top-right" />
    </PanelShell>
  );
}
